import { STATIC_PLANS } from '../data/staticPlans';
import type { FreeCreditSchedule, PlanConfig } from '../services/subscriptionService';

import { getPlanBillingInterval } from './pricingPlans';

export interface PlanCreditSummary {
  unlimited: boolean;
  signupOnly: boolean;
  annual: boolean;
  upfront: number;
  daily: number;
  dailyGrantDays: number;
  total: number;
  /** Paid plans: the daily share of the spread varies a little with the month's length. */
  approximateDaily?: boolean;
  /** Free plan only: a larger daily grant for the first `boostDays` days of each monthly cycle. */
  boostDaily?: number;
  boostDays?: number;
  /** Free plan only: days in a full cycle; grants stop after it, so a 31st day adds none. */
  cycleDays?: number;
  expiresEachCycle?: boolean;
}

const nonNegative = (amount: number): number => Number.isFinite(amount) ? Math.max(0, amount) : 0;
const roundCredits = (amount: number): number => Math.round(amount * 100) / 100;

const freeSchedule = (plan: PlanConfig): FreeCreditSchedule | undefined => (
  plan.free_credit_schedule ?? STATIC_PLANS.free?.free_credit_schedule
);

const freeSummary = (plan: PlanConfig, schedule: FreeCreditSchedule): PlanCreditSummary => {
  const upfront = nonNegative(schedule.upfront ?? plan.monthly_credits);
  const tailDays = schedule.tail_days ?? Math.max(0, schedule.cycle_days - schedule.boost_days);
  const computedTotal = upfront + schedule.boost_daily * schedule.boost_days + schedule.daily * tailDays;

  return {
    unlimited: false, signupOnly: true, annual: false, upfront,
    daily: nonNegative(schedule.daily), dailyGrantDays: tailDays,
    boostDaily: nonNegative(schedule.boost_daily), boostDays: schedule.boost_days, cycleDays: schedule.cycle_days,
    expiresEachCycle: schedule.expires_each_cycle,
    total: roundCredits(schedule.estimated_total ?? computedTotal),
  };
};

/** Half of a paid month arrives with the payment; the rest is spread over the billing month. */
const paidSummary = (planKey: string, plan: PlanConfig, isAnnual: boolean): PlanCreditSummary => {
  const unlimited = plan.is_unlimited === true || planKey === 'unlimited';
  const months = planKey !== 'free' && getPlanBillingInterval(planKey, isAnnual) === 'annual' ? 12 : 1;
  const monthlyUpfront = nonNegative(plan.monthly_credits);
  const daily = planKey === 'free' || unlimited ? 0 : nonNegative(
    plan.daily_drip_credits ?? STATIC_PLANS[planKey]?.daily_drip_credits ?? 0,
  );
  const monthlyTotal = nonNegative(plan.monthly_total_credits ?? monthlyUpfront + daily * 30);

  return {
    unlimited, signupOnly: planKey === 'free', annual: months === 12, upfront: monthlyUpfront * months, daily,
    dailyGrantDays: months === 12 ? 365 : 30, approximateDaily: daily > 0, total: roundCredits(monthlyTotal * months),
  };
};

export const getPlanCreditSummary = (
  planKey: string, plan: PlanConfig, isAnnual: boolean,
): PlanCreditSummary | null => {
  if (planKey === 'enterprise' || planKey === 'byok') return null;
  const schedule = planKey === 'free' ? freeSchedule(plan) : undefined;

  return schedule ? freeSummary(plan, schedule) : paidSummary(planKey, plan, isAnnual);
};

export const formatPlanCredits = (amount: number): string => amount.toLocaleString('en-US', {
  maximumFractionDigits: 2,
});

/** "5 / day for 4 days, then 1 / day" after a free signup; "~7 / day" for a paid plan's spread. */
export const formatDailyCredits = (summary: PlanCreditSummary): string => {
  if (summary.boostDaily && summary.boostDays) {
    return `${formatPlanCredits(summary.boostDaily)} / day for ${summary.boostDays} days, then `
      + `${formatPlanCredits(summary.daily)} / day`;
  }
  if (summary.daily <= 0) return 'None';

  return summary.approximateDaily ? `~${formatPlanCredits(Math.round(summary.daily))} / day` : `${formatPlanCredits(summary.daily)} / day`;
};

export const isCreditAllowanceHighlight = (text: string): boolean => (
  /^(?:[\d,.]+ Credits|\+[\d,.]+ Bonus Credits|Unlimited Credits)/i.test(text)
);
